import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import img1 from '../../../assets/attendenceCarousel.png';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import ReactECharts from 'echarts-for-react';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

import { attendanceApi } from '../services/api';
import PersonIcon from '@mui/icons-material/Person';
import { EMPLOYEE_TYPE } from '../../../shared/constants/lookupMaps';

// Status color legend
const statusColors: Record<string, { bg: string; label: string }> = {
  present: { bg: 'bg-[#22c55e]', label: 'Present' },
  absent: { bg: 'bg-[#ef4444]', label: 'Absent' },
  notMarked: { bg: 'bg-[#eab308]', label: 'Not Marked' },
  holiday: { bg: 'bg-[#06b6d4]', label: 'Holiday' },
  today: { bg: 'bg-[#f97316]', label: 'Today' },
  invalidFuture: { bg: 'bg-[#9ca3af]', label: 'Invalid Future Date' },
  weekend: { bg: 'bg-[#3b82f6]', label: 'Weekend' },
};

export default function AttendanceRegister() {
  const carouselImages = [img1];
  const location = useLocation();
  const { empId: routeEmpId } = useParams<{ empId: string }>();
  const params = new URLSearchParams(location.search);
  // Get empId from route params first, then query string, then passed state
  const employeeId = routeEmpId || params.get('id') || location.state?.employee?.empId || '';
  // Get emptype from query params or passed state (for contractual employees)
  const empTypeParam = params.get('emptype') || location.state?.empType || '';

  const [activeTab, setActiveTab] = useState<'graphical' | 'register' | 'details' | 'profile'>('graphical');
  const [employeeProfile, setEmployeeProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [registerData, setRegisterData] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());


  // Fetch employee profile data
  useEffect(() => {
    const fetchEmployeeProfile = async () => {
      if (employeeId) {
        setLoading(true);
        try {
          const response = await attendanceApi.getEmployeeProfile(employeeId, empTypeParam === 'contractual' ? 'contractual' : undefined);
          // Contractual API returns { success, data: {...} }; regular returns data directly
          const isContractual = empTypeParam === 'contractual';
          const profileData = (response?.success !== undefined && response?.data) ? response.data : response;
          if (profileData) {
            // Helper to format date strings
            const fmtDate = (d?: string) => {
              if (!d) return '-';
              try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
              catch { return '-'; }
            };

            // Find highest academic qualification (sort by education level desc)
            const academics: any[] = profileData.academics || [];
            const highestAcademic = academics.length > 0
              ? [...academics].sort((a, b) => Number(b.emp_education) - Number(a.emp_education))[0]
              : null;

            // Resolve fields — contractual uses different key names than regular
            const genderVal = profileData.emp_gender ?? profileData.gender;
            const empGender = genderVal === 1 ? 'Male' : genderVal === 2 ? 'Female' : (genderVal === 0 ? 'Not specified' : '-');

            // Map API data to display format (handles both regular and contractual structures)
            const mappedProfile = {
              name: profileData.emp_name || profileData.empName || 'N/A',
              empType: isContractual ? 'Contractual' : 'Regular',
              designation: profileData.present_designation_name || profileData.post_name || '-',
              subject: profileData.present_subject_name || profileData.subject_name || '-',
              email: profileData.emp_email || profileData.emailID || '-',
              phone: (profileData.emp_mobile_no && profileData.emp_mobile_no !== 0)
                ? String(profileData.emp_mobile_no)
                : (profileData.mobileNo || '-'),
              photo: profileData.emp_photo && profileData.emp_photo !== 'NA'
                ? `https://pis.kvs.gov.in/img/ProImage/${profileData.emp_photo}`
                : null,
              empCode: profileData.emp_code || profileData.tempEmpID || '-',
              empId: profileData.id || profileData.tempEmpID || '-',
              // Highest qualification from academics array
              qualification: highestAcademic?.emp_name_of_exam || highestAcademic?.emp_qualification_name || '-',
              university: highestAcademic?.emp_university_name || '-',
              passingYear: highestAcademic?.emp_passing_year || '-',
              // School / RO info
              kvName: profileData.present_school_name || profileData.kvName || '-',
              kvCode: profileData.present_kvcode || '-',
              udiseCode: profileData.present_udise_code || profileData.udise_code || '-',
              roName: profileData.present_ro_name || profileData.roName || '-',
              // Personal details
              empGender,
              dob: fmtDate(profileData.emp_dob || profileData.dob),
              bloodGroup: profileData.emp_blood_group || '-',
              category: profileData.category_name || profileData.social_category_name || '-',
              hometown: profileData.emp_hometown || '-',
              address: profileData.emp_postaladdress || profileData.address || '-',
              pincode: profileData.emp_pincode ? String(profileData.emp_pincode) : '-',
              fatherName: profileData.emp_father_name || '-',
              motherName: profileData.emp_mother_name || '-',
              // Service info
              joiningDate: fmtDate(profileData.present_joiningdate || profileData.dateOfJoining),
              initialJoiningDate: fmtDate(profileData.initial_joiningdate),
              initialDesignation: profileData.initial_designation_name || '-',
              // Pay
              payLevel: profileData.payDetails?.present_paylevel ? `Level ${profileData.payDetails.present_paylevel}` : '-',
              payRange: profileData.payDetails?.pay_range || '-',
              // Arrays for sub-sections
              academics: academics,
              serviceHistory: profileData.serviceHistory || [],
              familyDetails: profileData.familyDetails || [],
              professionalQualifications: profileData.professionalQualifications || [],
              cpdTrainings: profileData.cpdTrainings || [],
              // Attendance summary (contractual only)
              attendanceSummary: profileData.attendanceSummary || null,
              // Spouse Details and Data Consistency
              spouseDetails: profileData.spouseDetails || null,
              dataConsistency: profileData.dataConsistency || null,
            };
            setEmployeeProfile(mappedProfile);
            // For contractual employees, attendance data is embedded in the profile response
            if (isContractual && profileData.attendanceHistory) {
              setRegisterData(normalizeRegisterData(profileData.attendanceHistory));
            }
          } else {
            setEmployeeProfile(null);
          }
        } catch (error) {
          console.error('Error fetching employee profile:', error);
          setEmployeeProfile(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchEmployeeProfile();
  }, [employeeId]);

  // Fetch register data when employeeId, month, or year changes (skipped for contractual — data is embedded in profile)
  useEffect(() => {
    const fetchRegisterData = async () => {
      if (employeeId && selectedMonth && selectedYear && empTypeParam !== 'contractual') {
        try {
          const year = String(selectedYear).slice(-2); // Get last 2 digits of year
          const response = await attendanceApi.getEmployeeRegister(employeeId, selectedMonth, Number(year));
          if (response && response.success && response.data) {
            setRegisterData(normalizeRegisterData(response.data));
          } else {
            setRegisterData([]);
          }
        } catch (error) {
          console.error('Error fetching register data:', error);
          setRegisterData([]);
        }
      }
    };

    fetchRegisterData();
  }, [employeeId, selectedMonth, selectedYear]);

  // Helper function to get employee type text


  const getEmployeeTypeText = (empType: number | string) => {
    return (EMPLOYEE_TYPE as any)[String(empType)] || 'Staff';
  };

  // Format a Date to display string like '01 Dec 25'
  const formatDateStr = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day} ${month} ${year}`;
  };

  const normalizeRegisterData = (data: any): any[] => {
    const extractArray = (value: any) => Array.isArray(value) ? value : [];
    // 1. If data itself is an array
    const validRecords = extractArray(data).filter((item: any) => item && item.attendanceDate);
    if (validRecords.length > 0) return validRecords;
    // 2. If data.attendance is an array
    const attendanceRecords = extractArray(data?.attendance).filter((item: any) => item && item.attendanceDate);
    if (attendanceRecords.length > 0) return attendanceRecords;
    // 3. If data.data is an array
    const nestedRecords = extractArray(data?.data).filter((item: any) => item && item.attendanceDate);
    return nestedRecords;
  };

  const getRegisterDataArray = (): any[] => {
    const normalized = normalizeRegisterData(registerData);
    return Array.isArray(normalized) ? normalized : [];
  };

  // Transform register API data to graphical data format
  const transformToGraphicalData = () => {
    const normalizedData = getRegisterDataArray();
    if (!Array.isArray(normalizedData) || normalizedData.length === 0) return [];

    // Group records by date (YYYY-MM-DD)
    const byDate = new Map<string, any[]>();
    normalizedData.forEach((record: any) => {
      const dateKey = String(record.attendanceDate || '').split('T')[0];
      if (!byDate.has(dateKey)) byDate.set(dateKey, []);
      byDate.get(dateKey)!.push(record);
    });

    // Build all days in selected month
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const today = new Date();
    const result: { date: string; segments: { start: number; end: number; status: string }[] }[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(selectedYear, selectedMonth - 1, day);
      const isFuture = d > today;
      if (isFuture) continue; // skip future dates

      const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = d.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const dateStr = formatDateStr(d);

      const records = byDate.get(dateKey);

      if (records && records.length > 0) {
        // Multiple records = multiple sessions
        const segments = records.flatMap((record: any) => {
          const calStatus = record.calendar?.status ?? 11;
          const isWe = calStatus === 12 || calStatus === 13 || record.calendar?.statusLabel?.toUpperCase() === 'SUNDAY';
          const isHolid = calStatus !== 11 && !isWe;

          if (isWe) {
            return [{ start: 0, end: 24, status: 'weekend' }];
          }
          if (isHolid) {
            return [{ start: 0, end: 24, status: 'holiday' }];
          }

          if (record.createdAt && record.updatedAt) {
            const createdAt = new Date(record.createdAt);
            const updatedAt = new Date(record.updatedAt);
            if (!isNaN(createdAt.getTime()) && !isNaN(updatedAt.getTime())) {
              const startHour = createdAt.getHours() + createdAt.getMinutes() / 60;
              const endHour = updatedAt.getHours() + updatedAt.getMinutes() / 60;
              const status = record.status === '1' || record.status === 1 ? 'present' : 'absent';
              return [{ start: startHour, end: Math.max(startHour + 1, endHour), status }];
            }
          }

          // Fallback when createdAt/updatedAt are not available:
          const statusVal = record.status;
          if (statusVal === '1' || statusVal === 1) {
            return [{ start: 9, end: 17, status: 'present' }];
          } else if (statusVal === '0.5' || statusVal === 0.5) {
            return [
              { start: 9, end: 13, status: 'present' },
              { start: 13, end: 17, status: 'absent' },
            ];
          } else {
            return [{ start: 9, end: 17, status: 'absent' }];
          }
        });
        result.push({ date: dateStr, segments });
      } else if (isWeekend) {
        result.push({ date: dateStr, segments: [{ start: 0, end: 24, status: 'weekend' }] });
      } else {
        result.push({ date: dateStr, segments: [{ start: 0, end: 24, status: 'notMarked' }] });
      }
    }

    return result.reverse(); // Most recent first
  };

  // Transform register API data to calendar data format
  const transformToCalendarData = (): Record<number, string> => {
    const normalizedData = getRegisterDataArray();
    if (!Array.isArray(normalizedData) || normalizedData.length === 0) return {};

    const calendarMap: Record<number, string> = {};
    const today = new Date().getDate();
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    normalizedData.forEach((record: any) => {
      const datePart = record.attendanceDate.split('T')[0];
      const parts = datePart.split('-');
      if (parts.length !== 3) return;
      const day = Number(parts[2]);

      const calStatus = record.calendar?.status ?? 11;
      const isWe = calStatus === 12 || calStatus === 13 || record.calendar?.statusLabel?.toUpperCase() === 'SUNDAY';
      const isHolid = calStatus !== 11 && !isWe;

      let status = 'absent';
      if (isWe) {
        status = 'weekend';
      } else if (isHolid) {
        status = 'holiday';
      } else if (record.status === '1' || record.status === 1 || record.status === '0.5' || record.status === 0.5) {
        status = 'present';
      } else {
        status = 'absent';
      }
      calendarMap[day] = status;
    });

    // Process all days in the month
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = currentDate.getDay();
      const isToday = selectedMonth === currentMonth && selectedYear === currentYear && day === today;
      const isFuture = selectedYear > currentYear ||
        (selectedYear === currentYear && selectedMonth > currentMonth) ||
        (selectedYear === currentYear && selectedMonth === currentMonth && day > today);

      if (!calendarMap[day]) {
        // Mark today
        if (isToday) {
          calendarMap[day] = 'today';
        }
        // Mark future dates
        else if (isFuture) {
          calendarMap[day] = 'invalidFuture';
        }
        // Mark weekends
        else if (dayOfWeek === 0 || dayOfWeek === 6) {
          calendarMap[day] = 'weekend';
        }
        // Mark past weekdays without data as not marked (yellow)
        else {
          calendarMap[day] = 'notMarked';
        }
      }
    }

    return calendarMap;
  };

  // Transform register API data to attendance details format
  const transformToAttendanceDetails = () => {
    const normalizedData = getRegisterDataArray();
    if (normalizedData.length === 0) return [];

    return normalizedData.map((record: any, index: number) => {
      const datePart = record.attendanceDate.split('T')[0];
      const parts = datePart.split('-'); // ["2026", "02", "01"]
      const dateStr = parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}` : record.attendanceDate;

      return {
        sNo: index + 1,
        date: dateStr,
        locationIn: record.roID ? `RO ID: ${record.roID}, KV ID: ${record.kvID}` : '-',
        locationOut: record.roID ? `RO ID: ${record.roID}, KV ID: ${record.kvID}` : '-',
      };
    });
  };

  // Get graphical and calendar data from API
  const graphicalDataFromAPI = transformToGraphicalData();
  const calendarDataFromAPI = transformToCalendarData();
  const attendanceDetailsFromAPI = transformToAttendanceDetails();

  const graphicalDataToUse = graphicalDataFromAPI;
  const calendarDataToUse = calendarDataFromAPI;
  const attendanceDetailsToUse = attendanceDetailsFromAPI;

  // Status colors for chart
  const chartColors: Record<string, string> = {
    present: '#22c55e',
    absent: '#ef4444',
    notMarked: '#eab308', // yellow – no data for that day
    holiday: '#06b6d4',
    weekend: '#3b82f6',
  };

  // Generate series data for each segment
  const generateSeriesData = () => {
    const seriesData: any[] = [];
    graphicalDataToUse.forEach((dayData, dayIndex) => {
      dayData.segments.forEach((segment) => {
        seriesData.push({
          value: [segment.start, dayIndex, segment.end, dayIndex, segment.status],
          itemStyle: { color: chartColors[segment.status] || '#22c55e' },
        });
      });
    });
    return seriesData;
  };

  // Bar chart options for Graphical tab
  const barChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const start = params.value[0];
        const end = params.value[2];
        return `${String(start).padStart(2, '0')}:00 - ${String(end).padStart(2, '0')}:00`;
      },
    },
    grid: { left: 120, right: 40, top: 40, bottom: 20 },
    dataZoom: [
      {
        type: 'slider',
        yAxisIndex: 0,
        start: Math.max(0, 100 - (7 / Math.max(graphicalDataToUse.length, 7)) * 100), // Start from most recent 7 days
        end: 100,
        height: 480,  // increased for easier grabbing
        bottom: 15,
        showDetail: false,
        filterMode: 'none',
        brushSelect: false,
        zoomLock: true, // Lock zoom level - only allow scrolling
        moveOnMouseMove: false,
        moveOnMouseWheel: false,
        preventDefaultMouseMove: true,
        handleSize: '120%',  // larger handle
      },
      {
        type: 'inside',
        yAxisIndex: 0,
        start: Math.max(0, 100 - (7 / Math.max(graphicalDataToUse.length, 7)) * 100),
        end: 100,
        zoomLock: true, // Lock zoom level
        zoomOnMouseWheel: false, // Disable zoom on mouse wheel
        moveOnMouseMove: false,
        moveOnMouseWheel: true, // Allow scrolling with mouse wheel
      },
    ],
    xAxis: {
      type: 'value',
      name: 'Time',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
      min: 0,
      max: 24,
      interval: 2,
      axisLabel: { formatter: '{value}:00', fontSize: 13 },
      axisLine: { lineStyle: { width: 2 } },
      axisTick: { lineStyle: { width: 2 } },
      splitLine: { lineStyle: { width: 1 } },
    },
    yAxis: {
      type: 'category',
      name: 'Presence Graph (in hrs)',
      nameLocation: 'middle',
      nameGap: 90,
      nameRotate: 90,
      nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
      data: graphicalDataToUse.map(d => d.date),
      axisLabel: { fontSize: 13, fontWeight: 500 },
      axisLine: { lineStyle: { width: 2 } },
      axisTick: { lineStyle: { width: 2 } },
    },
    series: [
      {
        type: 'custom',
        renderItem: (params: any, api: any) => {
          const categoryIndex = api.value(1);
          const start = api.coord([api.value(0), categoryIndex]);
          const end = api.coord([api.value(2), categoryIndex]);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const _status = api.value(4);
          const height = 24;
          const width = end[0] - start[0];
          // Always render a solid bar for all statuses
          return {
            type: 'rect',
            shape: {
              x: start[0],
              y: start[1] - height / 2,
              width: width,
              height: height,
            },
            style: api.style(),
          };
        },
        encode: {
          x: [0, 2],
          y: 1,
        },
        data: generateSeriesData(),
      },
    ],
  };

  // Render calendar for Register tab
  const renderCalendar = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const firstDayOfWeek = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const days = [];

    // Previous month trailing days (showing as gray/inactive)
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
    const prevMonthDays = new Date(prevYear, prevMonth, 0).getDate();

    for (let i = 0; i < firstDayOfWeek; i++) {
      const dayNum = prevMonthDays - firstDayOfWeek + i + 1;
      days.push(
        <div key={`prev-${i}`} className="h-20 flex items-center justify-center text-lg font-semibold text-gray-400 bg-gray-100 rounded-xl">
          {dayNum}
        </div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const status = calendarDataToUse[day] || 'absent';
      const colorClass = statusColors[status]?.bg || 'bg-gray-200';
      days.push(
        <div
          key={day}
          className={`h-20 flex items-center justify-center text-lg font-semibold text-white rounded-xl ${colorClass}`}
        >
          {String(day).padStart(2, '0')}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="min-h-screen flex flex-col gap-0 bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl px-4 pt-0 sm:pt-0">
        <Breadcrumb />
      </div>
      <main className="flex-grow w-full max-w-8xl mx-auto px-4 py-5 md:px-8 lg:px-16">

        {/* ── HERO PROFILE HEADER ──────────────────────────────── */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-6">
          <div className="bg-gradient-to-r from-[#6A1786] via-[#A020A0] to-[#E91E8C] px-8 py-6 flex items-center justify-between gap-4">
            {/* Left: photo + name/details */}
            <div className="flex items-center gap-6">
              {employeeProfile?.photo ? (
                <img
                  src={employeeProfile.photo}
                  alt={employeeProfile.name}
                  className="w-28 h-28 rounded-full border-4 border-white/60 object-cover flex-shrink-0 shadow-md"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span className="w-28 h-28 rounded-full border-4 border-white/60 bg-white/20 flex items-center justify-center flex-shrink-0 text-white shadow-md">
                  <PersonIcon style={{ fontSize: 56 }} />
                </span>
              )}
              {loading ? (
                <div className="text-white/80 text-sm animate-pulse">Loading profile…</div>
              ) : employeeProfile ? (
                <div className="text-white">
                  <h2 className="text-3xl font-extrabold leading-tight">{employeeProfile.name}</h2>
                  <p className="text-white/90 text-sm mt-2">Employee Code: <span className="font-semibold">{employeeProfile.empCode}</span></p>
                  <p className="text-white/90 text-sm">Designation: <span className="font-semibold">{employeeProfile.designation}</span></p>
                  <p className="text-white/90 text-sm">School: <span className="font-semibold">{employeeProfile.kvName}</span></p>
                  <p className="text-white/90 text-sm">Type: <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${employeeProfile.empType === 'Contractual' ? 'bg-orange-500' : 'bg-green-500'}`}>{employeeProfile.empType}</span></p>
                </div>
              ) : (
                <div className="text-white/70 text-sm">No employee data</div>
              )}
            </div>
            {/* Right: title */}
            <div className="text-right flex-shrink-0">
              <p className="text-white text-4xl font-extrabold tracking-wide drop-shadow">Basic Profile</p>
              <p className="text-white/80 text-base mt-1">Kendriya Vidyalaya Sangathan</p>
            </div>
          </div>
        </div>

        {/* ── TABS + CONTENT CARD ──────────────────────────────── */}
        <div className="bg-white rounded-xl shadow overflow-hidden">

          {/* Tab buttons */}
          <div className="flex flex-wrap border-b border-gray-200">
            {(['graphical', 'register', 'profile'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-20 py-3 text-md font-bold transition-colors ${
                  activeTab === tab ? 'bg-[#6A1786] text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab === 'graphical' ? 'Graphical' : tab === 'register' ? 'Register': 'Profile'}
              </button>
            ))}
          </div>

          {/* Month/Year selector – only for attendance tabs */}
          {activeTab !== 'profile' && (
            <div className="flex items-center gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50">
              <label className="text-sm font-medium text-gray-700">Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                  <option key={month} value={month}>
                    {new Date(2025, month - 1).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {/* Tab content area */}
          <div className="p-6">

            {/* GRAPHICAL TAB */}
            {activeTab === 'graphical' && (
              <div>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {Object.entries(chartColors).map(([key, color]) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: color }}></div>
                      <span className="text-xs text-gray-600 capitalize">
                        {key === 'officialDuty' ? 'Official Duty' : key === 'holiday' ? 'Weekend/Holiday' : key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
                <ReactECharts option={barChartOption} style={{ height: 500 }} />
              </div>
            )}

            {/* REGISTER TAB */}
            {activeTab === 'register' && (
              <div className="flex gap-6">
                <div className="w-48 flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Legend</p>
                  <div className="space-y-3">
                    {Object.entries(statusColors).map(([key, { bg, label }]) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded ${bg}`}></div>
                        <span className="text-sm text-gray-700">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-[#6A1786] text-center mb-4">
                    {new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long' })} {selectedYear}
                  </h3>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                      <div key={day} className="h-10 flex items-center justify-center text-sm font-bold text-white bg-[#6A1786] rounded">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendar()}
                  </div>
                </div>
              </div>
            )}

            {/* ATTENDANCE DETAILS TAB */}
            {/* {activeTab === 'details' && (
              <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-gradient-to-r from-[#481789] to-[#6A1786] text-white">
                      <th className="px-3 py-2 text-left font-semibold">S No.</th>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-left font-semibold">Location IN</th>
                      <th className="px-3 py-2 text-left font-semibold">Location OUT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceDetailsToUse.length > 0 ? (
                      attendanceDetailsToUse.map((row, idx) => (
                        <tr key={row.sNo} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#edccfc]'}>
                          <td className="px-3 py-2 text-gray-700">{row.sNo}</td>
                          <td className="px-3 py-2 text-gray-700">{row.date}</td>
                          <td className="px-3 py-2 text-gray-700 text-xs">{row.locationIn}</td>
                          <td className="px-3 py-2 text-gray-700 text-xs">{row.locationOut}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                          No attendance data available for the selected month
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )} */}

            {/* PROFILE TAB */}
            {activeTab === 'profile' && !employeeProfile && (
              <p className="text-center text-gray-400 py-10">No profile data available</p>
            )}

            {activeTab === 'profile' && employeeProfile && (
              <div className="space-y-6">

                {/* ── DATA CONSISTENCY WARNINGS ── */}
                {employeeProfile.dataConsistency?.issues && employeeProfile.dataConsistency.issues.length > 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl shadow-sm">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <span className="text-xl text-red-600 font-bold">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-bold text-red-800">
                          Data Consistency Issues Detected
                        </h3>
                        <div className="mt-1 text-sm text-red-700">
                          <ul className="list-disc pl-5 space-y-1">
                            {employeeProfile.dataConsistency.issues.map((issue: any, index: number) => (
                              <li key={index}>
                                {typeof issue === 'string' ? issue : JSON.stringify(issue)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── ATTENDANCE SUMMARY (contractual only) ── */}
                {employeeProfile.attendanceSummary && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#1D4ED8] to-[#3B82F6] text-white px-5 py-3">
                      <span className="text-xl">📊</span>
                      <h3 className="font-bold text-base tracking-wide">Attendance Summary</h3>
                    </div>
                    <div className="flex divide-x divide-gray-100">
                      <div className="flex-1 flex flex-col items-center py-6 gap-1">
                        <span className="text-3xl font-extrabold text-green-600">{employeeProfile.attendanceSummary.Present ?? 0}</span>
                        <span className="text-sm text-gray-500 font-medium">Present</span>
                      </div>
                      <div className="flex-1 flex flex-col items-center py-6 gap-1">
                        <span className="text-3xl font-extrabold text-yellow-500">{employeeProfile.attendanceSummary.HalfDay ?? 0}</span>
                        <span className="text-sm text-gray-500 font-medium">Half Day</span>
                      </div>
                      <div className="flex-1 flex flex-col items-center py-6 gap-1">
                        <span className="text-3xl font-extrabold text-red-500">{employeeProfile.attendanceSummary.Absent ?? 0}</span>
                        <span className="text-sm text-gray-500 font-medium">Absent</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── PROFILE DETAILS ── */}
                <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="flex items-center gap-3 bg-gradient-to-r from-[#3B82F6] via-[#6A1786] to-[#9333EA] text-white px-5 py-3">
                    <span className="text-white font-bold text-lg">◆</span>
                    <h3 className="font-bold text-base tracking-wide">Profile Details</h3>
                  </div>
                  <table className="min-w-full text-sm">
                    <tbody>
                      {([
                        ['KV Name',       employeeProfile.kvName,    'Employee Code', employeeProfile.empCode],
                        ['Name',          employeeProfile.name,      'Gender',        employeeProfile.empGender],
                        ['DOB',           employeeProfile.dob,       'Email',         employeeProfile.email],
                        ['Mobile',        employeeProfile.phone !== '-' ? employeeProfile.phone : 'N/A', 'Designation', employeeProfile.designation],
                        ['Subject',       employeeProfile.subject,   'Region',        employeeProfile.roName],
                        ['Category',      employeeProfile.category,  'Employee Type', employeeProfile.empType],
                        ['Blood Group',   employeeProfile.bloodGroup,'Hometown',      employeeProfile.hometown],
                        ['Joining Date',  employeeProfile.joiningDate,'Pay Level',    employeeProfile.payLevel],
                        ['Pay Range',     employeeProfile.payRange,  'KV Code',       employeeProfile.kvCode],
                        ['Father Name',   employeeProfile.fatherName,'Mother Name',   employeeProfile.motherName],
                      ] as [string, string, string, string][]).map(([l1, v1, l2, v2], i) => (
                        <tr key={l1} className={i % 2 === 0 ? 'bg-white' : 'bg-[#EFF6FF]'}>
                          <td className="px-4 py-2.5 font-semibold text-[#2563EB] w-1/6 whitespace-nowrap">{l1}</td>
                          <td className="px-4 py-2.5 text-gray-800 w-1/3">{v1 || '—'}</td>
                          <td className="px-4 py-2.5 font-semibold text-[#2563EB] w-1/6 whitespace-nowrap border-l border-gray-100">{l2}</td>
                          <td className="px-4 py-2.5 text-gray-800 w-1/3">{v2 || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── EXPERIENCE / SERVICE HISTORY ── */}
                {employeeProfile.serviceHistory?.length > 0 && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#059669] to-[#10B981] text-white px-5 py-3">
                      <span className="text-xl">🏫</span>
                      <h3 className="font-bold text-base tracking-wide">Experience</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-[#D1FAE5] text-[#065F46]">
                            <th className="px-4 py-2.5 text-left font-semibold">School Name</th>
                            <th className="px-4 py-2.5 text-center font-semibold">From</th>
                            <th className="px-4 py-2.5 text-center font-semibold">To</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Position</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Transfer Ground</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeProfile.serviceHistory.map((sh: any, i: number) => {
                            const fmtD = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                            return (
                              <tr key={sh.id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FDF4]'}>
                                <td className="px-4 py-2.5 text-gray-800 font-medium">{sh.school_name || sh.kvName || '-'}</td>
                                <td className="px-4 py-2.5 text-center text-gray-600">{fmtD(sh.all_fromdate)}</td>
                                <td className="px-4 py-2.5 text-center font-medium">
                                  {sh.all_todate
                                    ? <span className="text-gray-600">{fmtD(sh.all_todate)}</span>
                                    : <span className="text-green-600 font-semibold">Present</span>}
                                </td>
                                <td className="px-4 py-2.5 text-gray-700">{sh.designation_name || '-'}</td>
                                <td className="px-4 py-2.5 text-gray-600">{sh.transfer_ground || sh.reason || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── ACADEMIC QUALIFICATIONS ── */}
                {employeeProfile.academics?.length > 0 && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-5 py-3">
                      <span className="text-xl">🎓</span>
                      <h3 className="font-bold text-base tracking-wide">Academic Qualifications</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-[#F3E8FF] text-[#6D28D9]">
                            <th className="px-4 py-2.5 text-left font-semibold">#</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Exam / Degree</th>
                            <th className="px-4 py-2.5 text-left font-semibold">University / Board</th>
                            <th className="px-4 py-2.5 text-center font-semibold">Year</th>
                            <th className="px-4 py-2.5 text-center font-semibold">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeProfile.academics.map((ac: any, i: number) => (
                            <tr key={ac.id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAF5FF]'}>
                              <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                              <td className="px-4 py-2.5 text-gray-800 font-medium">{ac.emp_name_of_exam || ac.emp_qualification_name || '-'}</td>
                              <td className="px-4 py-2.5 text-gray-700">{ac.emp_university_name || '-'}</td>
                              <td className="px-4 py-2.5 text-center text-gray-700">{ac.emp_passing_year || '-'}</td>
                              <td className="px-4 py-2.5 text-center font-semibold text-gray-700">{ac.emp_marks_percentage ? `${ac.emp_marks_percentage}%` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── FAMILY DETAILS ── */}
                {employeeProfile.familyDetails?.length > 0 && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white px-5 py-3">
                      <span className="text-xl">👨‍👩‍👧</span>
                      <h3 className="font-bold text-base tracking-wide">Family Details</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-[#FFF7ED] text-[#C2410C]">
                            <th className="px-4 py-2.5 text-left font-semibold">S.No</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Name</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Relationship</th>
                            <th className="px-4 py-2.5 text-center font-semibold">DOB</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Occupation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeProfile.familyDetails.map((fd: any, i: number) => (
                            <tr key={fd.id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF7ED]'}>
                              <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                              <td className="px-4 py-2.5 text-gray-800 font-medium">{fd.title ? `${fd.title} ` : ''}{fd.name || '-'}</td>
                              <td className="px-4 py-2.5 text-gray-700">{fd.relation || fd.relationship || '-'}</td>
                              <td className="px-4 py-2.5 text-center text-gray-700">{fd.dob ? new Date(fd.dob).toLocaleDateString('en-IN') : '-'}</td>
                              <td className="px-4 py-2.5 text-gray-700">{fd.occupation || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── SPOUSE DETAILS ── */}
                {employeeProfile.spouseDetails && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#EC4899] to-[#F43F5E] text-white px-5 py-3">
                      <span className="text-xl">👩‍❤️‍👨</span>
                      <h3 className="font-bold text-base tracking-wide">Spouse Details</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <tbody>
                          {(() => {
                            const formatVal = (val: any) => {
                              if (val === null || val === undefined || val === 0 || val === '0' || val === '') return '—';
                              return String(val);
                            };
                            const isGovt = employeeProfile.spouseDetails.is_govt_service === 1 || employeeProfile.spouseDetails.is_govt_service === '1';
                            return (
                              <>
                                <tr className="bg-white">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Spouse Name</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.spouse_name)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">In Gov. Service</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{isGovt ? 'Yes' : 'No'}</td>
                                </tr>
                                <tr className="bg-[#FFF1F2]">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Organization Name</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.org_name)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">Spouse Emp Code</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.spouse_emp_code)}</td>
                                </tr>
                                <tr className="bg-white">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Designation</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.designation)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">Subject</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.subject)}</td>
                                </tr>
                                <tr className="bg-[#FFF1F2]">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Posting Place</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.posting_place)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">Unit</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.unit)}</td>
                                </tr>
                                <tr className="bg-white">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Region</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.region)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">ZIET</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.ziet)}</td>
                                </tr>
                                <tr className="bg-[#FFF1F2]">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">School</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.school)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">Other Org Name</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.other_org_name)}</td>
                                </tr>
                                <tr className="bg-white">
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap">Other Org Post</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.other_org_post)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-[#E11D48] w-1/6 whitespace-nowrap border-l border-gray-100">Other Org Posting Place</td>
                                  <td className="px-4 py-2.5 text-gray-800 w-1/3">{formatVal(employeeProfile.spouseDetails.other_org_posting_place)}</td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                 {/* ── PROFESSIONAL QUALIFICATIONS ── */}
                 {employeeProfile.professionalQualifications?.length > 0 && (
                   <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                     <div className="flex items-center gap-3 bg-gradient-to-r from-[#0891B2] to-[#06B6D4] text-white px-5 py-3">
                       <span className="text-xl">📋</span>
                       <h3 className="font-bold text-base tracking-wide">Professional Qualifications</h3>
                     </div>
                     <div className="overflow-x-auto">
                       <table className="min-w-full text-sm">
                         <thead>
                           <tr className="bg-[#ECFEFF] text-[#0E7490]">
                             <th className="px-4 py-2.5 text-left font-semibold">#</th>
                             <th className="px-4 py-2.5 text-left font-semibold">Education Level</th>
                             <th className="px-4 py-2.5 text-left font-semibold">Board / University</th>
                             <th className="px-4 py-2.5 text-center font-semibold">Duration</th>
                             <th className="px-4 py-2.5 text-center font-semibold">Year</th>
                             <th className="px-4 py-2.5 text-center font-semibold">Marks</th>
                             <th className="px-4 py-2.5 text-center font-semibold">%</th>
                           </tr>
                         </thead>
                         <tbody>
                           {employeeProfile.professionalQualifications.map((pq: any, i: number) => (
                             <tr key={pq.id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#ECFEFF]'}>
                               <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                               <td className="px-4 py-2.5 text-gray-700">{pq.prof_education_name || '-'}</td>
                               <td className="px-4 py-2.5 text-gray-800">{pq.prof_board_univ_name || '-'}</td>
                               <td className="px-4 py-2.5 text-center text-gray-700">{pq.prof_duration ? `${pq.prof_duration} mo` : '-'}</td>
                               <td className="px-4 py-2.5 text-center text-gray-700">{pq.prof_year || '-'}</td>
                               <td className="px-4 py-2.5 text-center text-gray-700">{pq.prof_all_marks && pq.prof_tot_marks ? `${pq.prof_all_marks}/${pq.prof_tot_marks}` : '-'}</td>
                               <td className="px-4 py-2.5 text-center font-semibold text-gray-700">{pq.prof_percent ? `${pq.prof_percent}%` : '-'}</td>
                             </tr>
                           ))}
                         </tbody>
                       </table>
                     </div>
                   </div>
                 )}

                {/* ── CPD TRAININGS ── */}
                {employeeProfile.cpdTrainings?.length > 0 && (
                  <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-[#DC2626] to-[#EF4444] text-white px-5 py-3">
                      <span className="text-xl">🎯</span>
                      <h3 className="font-bold text-base tracking-wide">CPD Trainings</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-[#FEE2E2] text-[#991B1B]">
                            <th className="px-4 py-2.5 text-left font-semibold">#</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Course Name</th>
                            <th className="px-4 py-2.5 text-center font-semibold">Academic Year</th>
                            <th className="px-4 py-2.5 text-center font-semibold">Start Date</th>
                            <th className="px-4 py-2.5 text-center font-semibold">End Date</th>
                            <th className="px-4 py-2.5 text-center font-semibold">Hours</th>
                            <th className="px-4 py-2.5 text-center font-semibold">Grade</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Organized By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeProfile.cpdTrainings.map((training: any, i: number) => {
                            const fmtD = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                            const orgNames: Record<number, string> = {
                              3: 'Cluster', 4: 'School', 6: 'CIET', 16: 'Sahodaya', 1: 'KVS'
                            };
                            return (
                              <tr key={training.id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FEF2F2]'}>
                                <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                                <td className="px-4 py-2.5 text-gray-800 font-medium">{training.course_name || '-'}</td>
                                <td className="px-4 py-2.5 text-center text-gray-700">{training.academicYear || '-'}</td>
                                <td className="px-4 py-2.5 text-center text-gray-700">{fmtD(training.start_date)}</td>
                                <td className="px-4 py-2.5 text-center text-gray-700">{fmtD(training.end_date)}</td>
                                <td className="px-4 py-2.5 text-center font-semibold text-gray-700">{training.total_training_hour || '-'}</td>
                                <td className="px-4 py-2.5 text-center text-gray-700">{training.gradeRate || '-'}</td>
                                <td className="px-4 py-2.5 text-gray-700 text-xs">{orgNames[training.organized_by] || training.otherOrgniger || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!employeeProfile.academics?.length &&
                  !employeeProfile.serviceHistory?.length &&
                  !employeeProfile.familyDetails?.length &&
                  !employeeProfile.professionalQualifications?.length &&
                  !employeeProfile.cpdTrainings?.length && (
                  <p className="text-center text-gray-400 py-10">No additional profile data available</p>
                )}

              </div>
            )}

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
